import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import {
    useState,
    useRef,
    useCallback,
    useEffect,
} from 'react';
import styles from './ConfirmDialogWithComment.module.scss';
import { getErrorMessage } from '@/shared/lib/errors';
import { isBadRequestError } from '@/core/api/badRequestError';
import { RequestError } from '@/shared/ui/RequestState';
import { setConfirmDialogHandler, type ConfirmDialogWithCommentProps } from './confirmDialogState';

/** Отображает подтверждение действия с комментарием. */
export const ConfirmDialogWithCommentRoot = () => {
    const [visible, setVisible] = useState(false);
    const [props, setProps] = useState<ConfirmDialogWithCommentProps | null>(null);
    const [comment, setComment] = useState('');
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const show = useCallback((p: ConfirmDialogWithCommentProps) => {
        setProps(p);
        setComment('');
        setError(false);
        setSubmissionError(null);
        setVisible(true);
        setTimeout(() => inputRef.current?.focus(), 50);
    }, []);

    useEffect(() => {
        setConfirmDialogHandler(show);

        return () => setConfirmDialogHandler(null);
    }, [show]);

    /** Подтверждает выбранное действие. */
    const handleAccept = async () => {
        if (loading) return;
        if (props?.showCommentField !== false && !comment.trim()) {
            setError(true);
            inputRef.current?.focus();

            return;
        }
        setLoading(true);
        setSubmissionError(null);
        try {
            await props?.onConfirm(props?.showCommentField !== false
                ? comment
                : undefined);
            setVisible(false);
        } catch (error: unknown) {
            if (isBadRequestError(error)) return;
            setSubmissionError(getErrorMessage(error, 'Не удалось выполнить действие'));
        } finally {
            setLoading(false);
        }
    };

    /** Закрывает подтверждение, когда действие завершено */
    const handleHide = () => {
        if (!loading) setVisible(false);
    };

    const footer = (
        <div>
            <Button
                label={props?.rejectLabel || "Отмена"}
                icon="pi pi-times"
                onClick={handleHide}
                className="p-button-text"
                disabled={loading}
            />
            <Button
                label={props?.acceptLabel || "Подтвердить"}
                icon="pi pi-check"
                onClick={handleAccept}
                loading={loading}
                autoFocus
            />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={handleHide}
            header={props?.header}
            footer={footer}
            className={error
                ? styles.dialogError
                : undefined
            }
            closable={!loading}
            closeOnEscape={!loading}
        >
            <div className={styles.dialogContent}>
                <p className={styles.dialogMessage}>{props?.message}</p>
                {submissionError &&
                    <RequestError
                        message={submissionError}
                        compact
                    />
                }
                {props?.showCommentField !== false && (
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder={props?.commentPlaceholder || "Укажите причину"}
                        aria-label={props?.commentPlaceholder || 'Укажите причину'}
                        aria-invalid={error}
                        disabled={loading}
                        className={`${styles.commentInput} ${error
                            ? styles.commentInputError
                            : ''}`}
                        value={comment}
                        onChange={(e) => {
                            setComment(e.target.value);
                            setError(false);
                        }}
                    />
                )}
            </div>
        </Dialog>
    );
};
